import { MeetingProvider, useAuthContext } from "@kalyzee/kast-web-sdk";
import { useURL } from "@kalyzee/kast-web-sdk/web-components";
import React, { useEffect } from "react";
import { Navigate, Route } from "react-router-dom";
import { JoinPage } from "../pages/master/student/join/join";
import LoginRedirectionRoute from "../components/navigation/LoginRedirection";
import { NavigateSlave } from "../components/navigation/navigateSlave";
import { generateRoute } from "../components/navigation/Page";
import { PAGE_PATHS, PageMasterMode, PageSlaveMode } from "../components/navigation/PageContext";
import PrivateRoute from "../components/navigation/PrivateRoute";
import PublicRedirectionRoute from "../components/navigation/PublicRedirection";
import PublicRoute from "../components/navigation/PublicRoute";
import MasterRouteContainer from "../components/navigation/routes/MasterRouteContainer";
import SlaveRouteContainer from "../components/navigation/routes/SlaveRouteContainer";
import ConnexionView from "../components/utils/ConnexionView";
import { useAppStoreContext } from "../contexts/appStore/context";
import { SessionMode, StoreUtilsHooks } from "../contexts/appStore/hooks";
import { useAppStoreSelector } from "../contexts/appStore/selectors";
import { useAuthRedirects } from "../helpers/authRedirects";
import { useLogger } from "../helpers/logger";
import { appMessage } from "../helpers/message";
import { getGlobalQuery, getGlobalQueryLabel, NavigateGlobalQuery } from "../helpers/navigate";
import { loadSettings, Settings } from "../helpers/settings";
import { setToken, TokenType } from "../helpers/storage";
import { meetingsContext } from "../hooks/meeting";
import { useSettings } from "../hooks/settings";
import { MeetingListStudentPage } from "../pages/master/student/list/list";
import { MeetingDashboardStudentPage } from "../pages/master/student/meeting/connected/dashboard";
import ForbiddenPage from "../pages/public/forbidden/forbidden";
import LoginPage from "../pages/public/login/login";
import LogoutPage from "../pages/public/logout/logout";
import MeetingSlavePage from "../pages/slave/meeting/meeting";

import { AppLoadingView } from "../components/utils/AppLoadingView";
import { LoadingView } from "../components/utils/LoadingView";
import { useSdkContext } from "../contexts/sdk/context";
import styles from "./App.module.css";
import { MeetingInitPage } from "../pages/master/student/meeting/init";
import { MeetingLobbyPage } from "../pages/master/student/lobby/lobby";

function App() {
  const url = useURL();
  const store = useAppStoreContext();

  const mode = useAppStoreSelector(state => state.mode);
  const user = useAppStoreSelector(state => state.user);
  const userFetchStatus = useAppStoreSelector(state => state.userFetchStatus);

  const fetchUser = StoreUtilsHooks.useFetchUser(store);

  const [settings, setSettings] = useSettings();
  const logger = useLogger();
  const { status: authStatus } = useAuthContext();

  const sdkCtx = useSdkContext();

  useAuthRedirects();

  useEffect(() => {
    if (getGlobalQuery(url.href, NavigateGlobalQuery.SLAVE) && mode !== SessionMode.SLAVE) {
      store.dispatch("setMode", SessionMode.SLAVE);
    } else if (!mode) store.dispatch("setMode", SessionMode.MASTER);
    const screenIndex = getGlobalQuery(url.href, NavigateGlobalQuery.SCREEN_INDEX);
    const screenName = getGlobalQuery(url.href, NavigateGlobalQuery.SCREEN_NAME);
    const screenLayoutId = getGlobalQuery(url.href, NavigateGlobalQuery.SCREEN_LAYOUT_ID);
    if (screenIndex !== undefined || screenName || screenLayoutId !== undefined) {
      store.dispatch("setScreen", {
        index: screenIndex,
        name: screenName,
        layoutId: screenLayoutId,
      });
    }
    const disableGoogleLogin = getGlobalQuery(url.href, NavigateGlobalQuery.DISABLE_GOOGLE_LOGIN);
    if (disableGoogleLogin !== undefined) store.dispatch("setDisableGoogleLogin", disableGoogleLogin);
    const token = getGlobalQuery(url.href, NavigateGlobalQuery.TOKEN);
    const refreshToken = getGlobalQuery(url.href, NavigateGlobalQuery.REFRESH_TOKEN);
    if (token) {
      setToken(token, TokenType.Normal);
      setToken(refreshToken, TokenType.Refresh);
      url.searchParams.delete(getGlobalQueryLabel(NavigateGlobalQuery.TOKEN));
      url.searchParams.delete(getGlobalQueryLabel(NavigateGlobalQuery.REFRESH_TOKEN));
      window.location.href = url.href;
    }
  }, [url, mode]);

  useEffect(() => {
    const microphoneListener = (mute: boolean) => {
      store.dispatch("setMicrophone", mute);
    };
    appMessage.addEventListener("microphone", microphoneListener);
    return () => {
      appMessage.removeEventListener("microphone", microphoneListener);
    };
  }, []);

  useEffect(() => {
    appMessage.getMicrohoneData();
    navigator.mediaDevices?.enumerateDevices().then((data) => logger.log("devices : ", data));
    return () => {
      appMessage.close();
    };
  }, []);

  useEffect(() => {
    appMessage.ready(settings.secret?.remotePassword ?? undefined);
  }, [settings.secret?.remotePassword]);

  useEffect(() => {
    const getListener = () => appMessage.postSettings(loadSettings());
    const setListener = (data: Partial<Settings>) => {
      const newSettings = setSettings(data, { save: true });
      logger.log("localstorageSetSettings : ", newSettings);
      appMessage.postSettings(newSettings);
    };
    appMessage.addEventListener("localstorageGetSettings", getListener);
    appMessage.addEventListener("localstorageSetSettings", setListener);
    return () => {
      appMessage.removeEventListener("localstorageGetSettings", getListener);
      appMessage.removeEventListener("localstorageSetSettings", setListener);
    };
  }, [setSettings]);

  const provideMeetingContext = (children: React.ReactNode) =>  (
    <MeetingProvider
      context={meetingsContext}
      sdkClient={sdkCtx.client}
      useChatApdapter
      userId={user?.id}
      incomingStreamSessionValidator={(meeting, mySession, incomingSession) => {
        return incomingSession.id !== mySession.id;
      }}
      logs={typeof settings.logs === "boolean" ? settings.logs : settings.logs?.meetingContext}
      options={{
        receptionManager: {
          ovenMediaSession: {
            options: {
              retryAfter: 500,
              video: { mode: "subscription", delayToDisableStream: 5000 },
              audio: true,
            },
          },
          logger: (typeof settings.logs === "boolean" ? settings.logs : settings.logs?.ovenMediaSession?.reception?.main) ? logger.log : undefined,
          webrtcLogger: (typeof settings.logs === "boolean" ? settings.logs : settings.logs?.ovenMediaSession?.reception?.webrtc) ? logger.log : undefined,
        },
        emissionManager: {
          sdp: {
            formatter: {
              offer: settings?.emission?.sdpFormatter?.offer,
              answer: settings?.emission?.sdpFormatter?.answer,
            },
          },
          logger: (typeof settings.logs === "boolean" ? settings.logs : settings.logs?.ovenMediaSession?.emission?.main) ? logger.log : undefined,
          webrtcLogger: (typeof settings.logs === "boolean" ? settings.logs : settings.logs?.ovenMediaSession?.emission?.webrtc) ? logger.log : undefined,
        },
      }}
    >
      {children}
    </MeetingProvider>
  );

  const privateRoutes = () => {
    if (!mode) return <LoadingView fullscreen />;

    if (mode === SessionMode.SLAVE) {
      return (
        <SlaveRouteContainer element={<PrivateRoute mode={mode} authStatus={authStatus} />}>
          {generateRoute(SessionMode.SLAVE, PageSlaveMode.MEETING, <MeetingSlavePage />)}
          <Route path="" element={<NavigateSlave to={PAGE_PATHS[mode][PageSlaveMode.MEETING]} />} />
          <Route path="*" element={<NavigateSlave to={PAGE_PATHS[mode][PageSlaveMode.MEETING]} />} />
        </SlaveRouteContainer>
      );
    }

    if (!user) return <AppLoadingView />;

    return (
      <MasterRouteContainer element={<PrivateRoute mode={mode} authStatus={authStatus} />}>
        {generateRoute(SessionMode.MASTER, PageMasterMode.LOGOUT, <LogoutPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.LOGIN, <LoginRedirectionRoute defaultTo={PAGE_PATHS[mode][PageMasterMode.LIST]} />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.LIST, <MeetingListStudentPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.JOIN, <JoinPage authenticated />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.LOBBY, <MeetingLobbyPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.MEETING, provideMeetingContext(<MeetingInitPage />), {
          children: (
            <>
              {generateRoute(SessionMode.MASTER, PageMasterMode.MEETING_DASHBOARD, <MeetingDashboardStudentPage />)}
            </>
          ),
        })}
        <Route path="" element={<Navigate to={PAGE_PATHS[mode][PageMasterMode.LIST]} />} />
        <Route path="*" element={<Navigate to={PAGE_PATHS[mode][PageMasterMode.LIST]} />} />
      </MasterRouteContainer>
    );
  };

  const publicRoutes = () => {
    if (!mode) return <LoadingView fullscreen />;
    if (mode === SessionMode.SLAVE || getGlobalQuery(url.href, NavigateGlobalQuery.SLAVE)) {
      return (
        <SlaveRouteContainer element={<PublicRoute mode={mode} authStatus={authStatus} />}>
          <Route path="" element={<PublicRedirectionRoute mode={mode} />} />
          <Route path="*" element={<PublicRedirectionRoute mode={mode} />} />
        </SlaveRouteContainer>
      );
    }
    return (
      <MasterRouteContainer element={<PublicRoute mode={mode} authStatus={authStatus} />}>
        {generateRoute(SessionMode.MASTER, PageMasterMode.LOGIN, <LoginPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.FORBIDDEN, <ForbiddenPage />)}
        {generateRoute(SessionMode.MASTER, PageMasterMode.JOIN, <JoinPage />)}
        <Route path="" element={<PublicRedirectionRoute mode={mode} />} />
        <Route path="*" element={<PublicRedirectionRoute mode={mode} />} />
      </MasterRouteContainer>
    );
  };

  const routes = () => {
    if (authStatus === "in") {
      if (user) return privateRoutes();
      return (
        <ConnexionView
          status={userFetchStatus}
          retry={() => {
            if (!user) {
              fetchUser();
              return;
            }
          }}
        />
      );
    }
    return publicRoutes();
  };

  return <div className={styles.background}>{routes()}</div>;
}

export default App;
